import { PhoneNumberUtil } from 'google-libphonenumber';
import { Button } from "react-bootstrap";
import { VALIDATIONS, VALUE, CLIENT_NAME, US_STATE, PROGRAM_TYPE, PROGRAM_TYPE_HOST_MARKER } from "./constants";

/**
 * Derives state code from hostname when the first subdomain ends with a valid 2-letter state code.
 * e.g. bcbsnm.enrolleevoice.com -> "NM", bcbstx.enrolleevoice.com -> "TX"
 */
export function getStateFromSubdomain() {
    if (typeof window === "undefined") return null;
    const hostname = window.location.hostname || "";
    const firstPart = hostname.split(".")[0] || "";
    const code = firstPart.slice(-2).toUpperCase();
    return code.length === 2 && US_STATE[code] ? code : null;
}

// The first host label is split on hyphens; a segment matching a marker selects that program type.
export function getProgramTypeFromSubdomain() {
    if (typeof window === "undefined") return PROGRAM_TYPE.DSNP;
    const hostname = window.location.hostname || "";
    const firstPart = hostname.split(".")[0] || "";
    const marked = firstPart.toLowerCase().split("-")
        .find(segment => Object.prototype.hasOwnProperty.call(PROGRAM_TYPE_HOST_MARKER, segment));
    return marked ? PROGRAM_TYPE_HOST_MARKER[marked] : PROGRAM_TYPE.DSNP;
}

const moment = require('moment');

export function optionValues(obj, includeBlank = false, placeholder = 'Select') {
    let optionArr = [];
    if (!obj || typeof obj !== 'object') {
        return optionArr;
    }
    if (includeBlank) {
        optionArr.push({ id: '', value: placeholder });
    }
    for (const [key, value] of Object.entries(obj)) {
        optionArr.push({ id: key, value: value });
    }
    return optionArr;
}

export function radioOptionValues(obj, keyAsLabel = false, keyAsValue = false) {
    let optionArr = [];
    for (const [key, value] of Object.entries(obj)) {
        optionArr.push({ id: key, label: keyAsLabel ? key : value, value: keyAsValue ? key : value });
    }
    return optionArr;
}

export const isPhoneNumberValid = (phoneNumber) => {
    if (isDevMode()) {
        if (phoneNumber) {
            // strip all chars except numbers
            let cleanedNumber = phoneNumber.replace(/[^\d]/g, '');
            let basicValidation = VALIDATIONS.Number.isPhone.test;
            return basicValidation.test(cleanedNumber);
        } else {
            return false;
        }
    }

    let valid = false;
    try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        valid = phoneUtil.isValidNumberForRegion(phoneUtil.parse(phoneNumber.toString(), 'US'), 'US');
    } catch (e) {
        valid = false;
    }

    return valid;
}

export const isEmpty = (value) => {
    return (
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && Object.keys(value).length === 0 && value.constructor === Object) ||
        (typeof value === "string" && value.trim().length === 0)
    )
}

export function isDevMode() {
    return process.env.NODE_ENV === 'development';
}

export function checkValid(val, reg) {
    return {
        valid: reg.test.test(val),
        error: reg.msg
    }
}

export function scrollToTop(pos = 0) {
    setTimeout(() => {
        window.scrollTo({
            top: pos,
            behavior: "instant",
        });
    }, 10);
}

export function toDate(dateString) {
    return dateString ? moment(dateString).format('MM/DD/yyyy') : '';
}

export const todayEndOfDay = () => {
    return moment().add(1, 'day').startOf('day');
}

export function greaterThanFormDateTime(dateString) {
    return dateString ? moment(dateString).add(1, 'day').format('YYYY-MM-DD') : '';
}

export function lessThanFormDateTime(dateString) {
    return dateString ? moment(dateString).subtract(1, 'day').format('YYYY-MM-DD') : '';
}

export function toFormDateTime(dateString) {
    return dateString ? moment(dateString).format('YYYY-MM-DD') : '';
}

export function isDateSameOrBefore(date1, date2) {
    return moment(date1).isSameOrBefore(date2);
}

export function checkWhiteSpace(val) {
    return /^\s+|\s{2,}|\s+$/.test(val);
}

export function getFormattedValue(value, type, operation = null) {
    if (type === 'date') {
        if (operation === VALUE.Min) {
            return greaterThanFormDateTime(value)
        } else if (operation === VALUE.Max) {
            return lessThanFormDateTime(value)
        }
        return toFormDateTime(value)
    } else if (checkWhiteSpace(value) && value.toString().trim() === '') { // Fix for: input value = multiple spaces
        return ''
    } else {
        return value ?? ''
    }
}

// we need to add a tag in storage container html file with class='custom-event-tag' and
// call this function
export function getHtmlCustomEventListener(clientName, variant, handleClick, className = 'custom-event-tag') {
    switch (clientName) {
        case CLIENT_NAME.BCPT:
            return {
                replace: (domNode) => {
                    if (domNode.attribs && domNode.attribs.class === className) {
                        return (
                            <div className="mb-2">
                                <Button variant={variant} className="px-lg-4" onClick={handleClick}>Submit Your Application</Button>
                            </div>
                        );
                    }
                },
            }
        default:
            return <></>;
    }
}

export function pickProps(obj, props) {
    return props.reduce(function(result, prop) {
        result[prop.trim()] = obj[prop.trim()];
        return result;
    }, {});
}

export function omitProps(obj, props) {
    const result = { ...obj };
    props.forEach(function(prop) {
        delete result[prop];
    });
    return result;
}

export function yesNoMaybeRadioOptions(tv) {
    return [
        { id: 'yes', label: tv("yes"), value: "yes" },
        { id: 'no', label: tv("no"), value: "no" },
        { id: 'maybe', label: tv("maybe"), value: "maybe" }
    ];
}

export function translatedYesNoValues(tv, options = []) {
    return [
        { id: 'Y', label: tv("yes"), value: "Y" },
        { id: 'N', label: tv("no"), value: "N" },
        ...options.map(option => ({
            id: option.id,
            label: tv(option.label),
            value: option.value
        }))
    ];
}

export function translatedRadioOptions(tv, obj) {
    return Object.entries(obj).map(([key, value]) => ({
        id: key,
        label: tv(value),
        value: key
    }));
}


export function getCurrentYear() {
    return new Date().getFullYear();
}
